import React, { useState, useEffect } from 'react';
import { lessonplanService } from '../../lib/services/lessonplan';
import CourseSchedule from './CourseSchedule';
import HomeworkManagement from './HomeworkManagement';
import CalendarView from './CalendarView';

const CourseDetailsSystem = ({ selectedItem, selectedDate, onBack }) => {
  const [currentTab, setCurrentTab] = useState('schedule'); // 'schedule', 'homework', 'calendar'
  const [courseDetails, setCourseDetails] = useState([]);
  const [homeworkAssignments, setHomeworkAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCourseData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [courses, homework] = await Promise.all([
        lessonplanService.getCourseDetails(selectedItem.id || selectedItem.class_id, selectedDate),
        lessonplanService.getHomeworkAssignments(selectedItem.id || selectedItem.class_id, selectedDate)
      ]);

      setCourseDetails(courses);
      setHomeworkAssignments(homework);
    } catch (err) {
      console.error('Error loading course data:', err);
      setError('載入課程資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  }, [selectedItem, selectedDate]);

  useEffect(() => {
    if (selectedItem && selectedDate) {
      loadCourseData();
    }
  }, [selectedItem, selectedDate, loadCourseData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayNames = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const dayName = dayNames[date.getDay()];
    
    return `${year}年${month}月${day}日 (${dayName})`;
  };

  if (loading) {
    return (
      <div className="course-details-system">
        <div className="course-details-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回時間表
          </button>
          <h2>課程詳情 - {formatDate(selectedDate)}</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入課程詳情中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-details-system">
        <div className="course-details-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回時間表
          </button>
          <h2>課程詳情 - {formatDate(selectedDate)}</h2>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadCourseData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="course-details-system">
      <div className="course-details-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回時間表
        </button>
        <h2>課程詳情 - {formatDate(selectedDate)}</h2>
        <div className="date-selector">
          <input 
            type="date" 
            value={selectedDate}
            onChange={(e) => {
              // This would need to be handled by parent component
              // For now, we'll just show the current date
            }}
            className="date-input"
          />
        </div>
      </div>

      <div className="course-details-nav">
        <button 
          className={`nav-tab ${currentTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setCurrentTab('schedule')}
        >
          📚 課程表
        </button>
        <button 
          className={`nav-tab ${currentTab === 'homework' ? 'active' : ''}`}
          onClick={() => setCurrentTab('homework')}
        >
          📝 家課
        </button>
        <button 
          className={`nav-tab ${currentTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setCurrentTab('calendar')}
        >
          📅 月曆
        </button>
      </div>

      <div className="course-details-content">
        {currentTab === 'schedule' && (
          <CourseSchedule 
            courseDetails={courseDetails}
            selectedDate={selectedDate}
          />
        )}

        {currentTab === 'homework' && (
          <HomeworkManagement 
            homeworkAssignments={homeworkAssignments}
            selectedDate={selectedDate}
          />
        )}

        {currentTab === 'calendar' && (
          <CalendarView 
            selectedItem={selectedItem}
            selectedDate={selectedDate}
            onDateSelect={(date) => {
              // This would need to be handled by parent component
              console.log('Date selected:', date);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CourseDetailsSystem;
